<template>
  <div class="swipe-row">
    <div class="swipe-actions" :class="{ visible: open || peek || dragX !== null }" :style="{ width: actionsWidth + 'px' }"><slot name="actions"></slot></div>
    <div
      class="swipe-content"
      :class="{ swipeable: !disabled, dragging: dragX !== null, peek }"
      :style="{ transform: `translateX(${offset}px)`, '--peek-x': `${-actionsWidth / 2}px` }"
      @animationend.self="$emit('peek-end')"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="reset"
      @click.capture="onClickCapture"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script>
const DRAG_START_PX = 8; // below this a press is still a tap

/**
 * SwipeRow — swipe the row left to reveal the actions slot behind it. open is v-model:open so
 * the parent can keep one row open at a time. peek plays a one-off slide that shows what is
 * behind the row. Vertical drags stay with the page (touch-action: pan-y).
 */
export default {
  name: "SwipeRow",
  props: {
    open: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    actionsWidth: { type: Number, default: 144 },
    peek: { type: Boolean, default: false },
  },
  emits: ["update:open", "peek-end"],
  data() {
    return {
      press: null, // where the pointer went down
      dragX: null, // row offset during a drag
      dragged: false, // the click that ends a drag is not a tap
    };
  },
  computed: {
    offset() { return this.dragX ?? (this.open ? -this.actionsWidth : 0); },
  },
  methods: {
    onPointerDown(e) {
      this.dragged = false;
      if (this.peek) this.$emit("peek-end");
      if (this.disabled || e.button !== 0) return;
      this.press = { x: e.clientX, offset: this.offset };
    },
    onPointerMove(e) {
      if (!this.press) return;
      const dx = e.clientX - this.press.x;
      if (this.dragX === null) {
        if (Math.abs(dx) < DRAG_START_PX) return;
        // Captured only now: from the press on, a tap on a button inside would lose its click
        e.currentTarget.setPointerCapture(e.pointerId);
        this.dragged = true;
      }
      this.dragX = Math.max(-this.actionsWidth, Math.min(0, this.press.offset + dx));
    },
    onPointerUp() {
      if (this.dragX !== null) this.$emit("update:open", this.dragX < -this.actionsWidth / 2);
      this.reset();
    },
    reset() {
      this.press = null;
      this.dragX = null;
    },
    onClickCapture(e) {
      if (!this.dragged) return;
      this.dragged = false;
      e.stopPropagation();
      e.preventDefault();
    },
  },
};
</script>

<style scoped>
.swipe-row { position: relative; overflow: hidden; border-radius: 0.5rem; }
.swipe-actions { position: absolute; top: 0; right: 0; bottom: 0; display: flex; visibility: hidden; transition: visibility 0s 0.2s; }
.swipe-actions.visible { visibility: visible; transition: none; }
.swipe-content { position: relative; transition: transform 0.2s; }
.swipe-content.swipeable { touch-action: pan-y; user-select: none; -webkit-user-select: none; }
.swipe-content.dragging { transition: none; }
.swipe-content.peek { animation: swipe-peek 1.6s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both; }
@keyframes swipe-peek {
  0% { transform: translateX(0); }
  28% { transform: translateX(calc(var(--peek-x) - 6px)); }
  38%, 66% { transform: translateX(var(--peek-x)); }
  100% { transform: translateX(0); }
}
</style>
